import React, { useMemo, useState } from "react";
import { G, UI, useStore, useIsGM, openWindow, memberName } from "../store";
import { create, update, remove, send } from "../net";
import type { Macro, Deck } from "../../shared/types";
import { Field, confirm, prompt, choose } from "../util";
import { sendChat } from "../chatSend";
import { cardFace, cardBack } from "../board/images";

export function CollectionPanel() {
  return (
    <div className="collection">
      <Macros />
      <Tables />
      <Decks />
    </div>
  );
}

function Macros() {
  const gm = useIsGM();
  const me = useStore(G, s => s.me);
  const macros = useStore(G, s => s.recs.macro);
  const [edit, setEdit] = useState<string | null>(null);
  const list = Object.values(macros).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <section>
      <div className="box-title">
        Macros
        <button className="mini" onClick={async () => {
          const name = await prompt("Macro name (no spaces)", "attack");
          if (!name) return;
          const id = await create("macro", { name: name.replace(/\s+/g, "-"), action: "/r 1d20+@{selected|bar1}", inBar: true });
          setEdit(id);
        }}>＋</button>
      </div>
      <div className="dim small">Run from chat with <code>#name</code>. Tick “bar” to show it in the macro bar below the map.</div>
      {list.map(m => (
        <div key={m.id} className="macro">
          <div className="row">
            <button className="rollable" onClick={() => sendChat(m.action)} title="Run">▶</button>
            <span className="grow">{m.name}{m.owner !== me && <span className="dim small"> · {memberName(m.owner)}</span>}</span>
            {m.owner === me && <button className="mini" onClick={() => setEdit(edit === m.id ? null : m.id)}>✎</button>}
            {(m.owner === me || gm) && <button className="mini danger" onClick={async () => { if (await confirm(`Delete macro ${m.name}?`)) remove("macro", m.id); }}>✕</button>}
          </div>
          {m.owner === me && (
            <div className="row gap-s">
              <label className="chk small"><input type="checkbox" checked={!!m.inBar} onChange={e => update("macro", m.id, { inBar: e.target.checked })} /> in bar</label>
              <label className="chk small" title="Show when a token is selected"><input type="checkbox" checked={!!m.tokenAction} onChange={e => update("macro", m.id, { tokenAction: e.target.checked })} /> token action</label>
              <label className="chk small" title="Visible to everyone in this game"><input type="checkbox" checked={!!m.shared} onChange={e => update("macro", m.id, { shared: e.target.checked })} /> shared</label>
            </div>
          )}
          {edit === m.id && (
            <div className="macro-edit">
              <Field value={m.name} onCommit={v => update("macro", m.id, { name: v.replace(/\s+/g, "-") })} />
              <Field multiline rows={4} value={m.action} onCommit={v => update("macro", m.id, { action: v })} />
              <div className="dim small">
                Examples: <code>/r 1d20+@{"{"}selected|dex_mod{"}"}</code> · <code>/em draws a sword</code> · <code>[[?{"{"}Modifier|0{"}"}+1d20]]</code> · <code>%{"{"}selected|Longsword{"}"}</code>
              </div>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function Tables() {
  const gm = useIsGM();
  const tables = useStore(G, s => s.recs.table);
  const list = Object.values(tables).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <section>
      <div className="box-title">
        Rollable tables
        {gm && <button className="mini" onClick={async () => {
          const name = await prompt("Table name", "Loot");
          if (!name) return;
          const id = await create("table", { name: name.replace(/[\[\]]/g, ""), items: [] });
          openWindow("table", id);
        }}>＋</button>}
      </div>
      {list.map(t => (
        <div key={t.id} className="row">
          <button className="rollable" onClick={() => sendChat(`/r 1t[${t.name}]`)}>🎲</button>
          <span className="grow link" onClick={() => openWindow("table", t.id)}>{t.name}</span>
          <span className="dim small">{t.items.length} items</span>
        </div>
      ))}
      {!list.length && <div className="dim small">{gm ? "Random encounters, loot, weather…" : "No shared tables."}</div>}
    </section>
  );
}

function Decks() {
  const gm = useIsGM();
  const decks = useStore(G, s => s.recs.deck);
  const members = useStore(G, s => s.members);
  const list = Object.values(decks);
  return (
    <section>
      <div className="box-title">
        Card decks
        {gm && <button className="mini" onClick={() => create("deck", { kind: "playing", name: "Playing Cards" })}>＋</button>}
      </div>
      {list.map(d => {
        const pileCount = typeof d.pile === "number" ? d.pile : d.pile.length;
        return (
          <div key={d.id} className="deck">
            <div className="row">
              <span className="grow">🂠 {gm ? <Field value={d.name} onCommit={v => update("deck", d.id, { name: v })} /> : d.name}</span>
              <span className="dim small">{pileCount} left</span>
            </div>
            <div className="row gap wrap">
              <button onClick={() => { send({ t: "deck", id: d.id, op: "draw" }); UI.set({ handOpen: true }); }}>Draw</button>
              {gm && <button onClick={() => send({ t: "deck", id: d.id, op: "shuffle" })}>Shuffle</button>}
              {gm && <button onClick={async () => {
                const to = await choose("Deal a card to…", members.map(m => ({ label: m.display, value: m.uid })));
                if (to) send({ t: "deck", id: d.id, op: "give", to });
              }}>Deal…</button>}
              {gm && <button onClick={async () => { if (await confirm("Recall all cards (hands and table) and reshuffle?")) send({ t: "deck", id: d.id, op: "recall" }); }}>Recall</button>}
              {gm && <label className="chk small"><input type="checkbox" checked={d.showPlayers} onChange={e => update("deck", d.id, { showPlayers: e.target.checked })} /> players</label>}
              {gm && <button className="mini danger" onClick={async () => { if (await confirm(`Delete deck ${d.name}?`)) remove("deck", d.id); }}>✕</button>}
            </div>
            {gm && Object.entries(d.hands).some(([, h]) => h.length) && (
              <div className="dim small">In hands: {Object.entries(d.hands).filter(([, h]) => h.length).map(([u, h]) => `${memberName(u)} ${h.length}`).join(" · ")}</div>
            )}
          </div>
        );
      })}
      {!list.length && <div className="dim small">{gm ? "Add a standard 54-card deck for games of chance, Deck of Many Things, initiative cards…" : "No decks."}</div>}
    </section>
  );
}

/** Tray with the local player's cards. */
export function Hand() {
  const decks = useStore(G, s => s.recs.deck);
  const me = useStore(G, s => s.me);
  const open = useStore(UI, s => s.handOpen);
  const cards = useMemo(() => {
    const out: { deck: Deck & { pile: any }; idx: number }[] = [];
    for (const d of Object.values(decks)) for (const idx of d.hands[me] || []) out.push({ deck: d, idx });
    return out;
  }, [decks, me]);
  if (!cards.length) return null;
  if (!open) return <button className="hand-toggle" onClick={() => UI.set({ handOpen: true })}>🂠 Hand ({cards.length})</button>;
  return (
    <div className="hand">
      <div className="hand-head">
        <span>Your hand — drag a card onto the table to play it (hold Alt to play face-down)</span>
        <button className="mini" onClick={() => UI.set({ handOpen: false })}>▾</button>
      </div>
      <div className="hand-cards">
        {cards.map(({ deck, idx }) => {
          const c = deck.cards[idx];
          const src = c?.src || cardFace(c?.name || "?").toDataURL();
          return (
            <div key={deck.id + idx} className="hand-card" draggable onDragStart={e => e.dataTransfer.setData("application/x-hearth-card", JSON.stringify({ deckId: deck.id, idx }))}>
              <img src={src} alt={c?.name} />
              <button className="mini" onClick={() => send({ t: "deck", id: deck.id, op: "show", idx })}>Show</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { cardBack };
